import { environment } from '@accredible-frontend-v2/envs';
import {
  AccountsRedirectionKey,
  AccredibleAccountsRedirectionService,
} from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleCookiesService } from '@accredible-frontend-v2/services/cookies';
import {
  AccredibleLanguageService,
  registerSupportedLocalesData,
} from '@accredible-frontend-v2/services/language';
import { addMicrosoftClarity } from '@accredible-frontend-v2/utils/microsoft-clarity';
import { addPostHog, identifyUserToPostHog } from '@accredible-frontend-v2/utils/posthog-helper';
import { WindowHelper } from '@accredible-frontend-v2/utils/window-helper';
import { DOCUMENT } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import { inject, Injectable } from '@angular/core';
import { LerAuthenticationApiService } from '@ler/domain';
import { PUBLIC_ROUTE_PREFIXES } from './public-route-prefixes';

const TOKEN_PARAM = 'token';
const MS_CLARITY_ID = 'vxmxy5jxes';

@Injectable()
export class LerLoadService {
  private readonly _document = inject<Document>(DOCUMENT);
  private readonly _authenticationApi = inject(LerAuthenticationApiService);
  private readonly _cookies = inject(AccredibleCookiesService);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _accountsRedirection = inject(AccredibleAccountsRedirectionService);

  initializeApp(): Promise<boolean> {
    return new Promise((resolve) => {
      this._setLanguages();

      addMicrosoftClarity(MS_CLARITY_ID);

      const postHogLocation = environment.name === 'production_eu' ? 'eu' : 'us';
      addPostHog(environment.lerPosthogId, postHogLocation, 'identified_only');

      resolve(true);
    });
  }

  validateAuthenticationToken(): Promise<boolean> {
    return new Promise((resolve) => {
      const urlParams = new URLSearchParams(this._document.location.search);
      const isPublicRoute = this._isPublicRoute();

      if (urlParams.has(TOKEN_PARAM)) {
        this._cookies.set(AccountsRedirectionKey.SESSION_TOKEN_COOKIE, urlParams.get(TOKEN_PARAM));
        this._checkSession(isPublicRoute).then((isLoggedIn) => resolve(isLoggedIn));

        WindowHelper.removeQueryParamFromUrl(TOKEN_PARAM, urlParams);
      } else if (this._cookies.check(AccountsRedirectionKey.SESSION_TOKEN_COOKIE)) {
        this._checkSession(isPublicRoute).then((isLoggedIn) => resolve(isLoggedIn));
      } else if (isPublicRoute) {
        resolve(true);
      } else {
        this._redirectToLogin();
        resolve(false);
      }
    });
  }

  private _checkSession(skipRedirectOnError = false): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this._authenticationApi.checkSession(environment.lerUrl).subscribe({
        next: (user) => {
          identifyUserToPostHog({ id: user.id, email: user.email, name: user.name });
          resolve(true);
        },
        error: () => {
          if (!skipRedirectOnError) {
            this._redirectToLogin();
          }
          resolve(false);
        },
      });
    });
  }

  private _isPublicRoute(): boolean {
    const path = this._document.location.pathname;
    return PUBLIC_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
  }

  private _redirectToLogin(): void {
    this._document.location.href = this._accountsRedirection.getLoginUrlOrigin('ler');
  }

  private _setLanguages(): void {
    registerSupportedLocalesData([
      {
        code: 'en',
        name: 'English (US)',
        matLib: localeEn,
      },
    ]);

    this._language.setLanguage('en');
  }
}
